import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { ApplicationText, AudioText } from '../_models/model';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import translations from '../languages/languages';
import { ApplicationTextType } from '../languages/IApplicationText';

type CallBack = (a: null) => void;

@Injectable({
  providedIn: 'root'
})
export class AudioPlayerService {
  soundClick: HTMLAudioElement;
  soundLevelFinished: HTMLAudioElement;
  soundGameFinished: HTMLAudioElement;
  soundSuccess: HTMLAudioElement;
  soundError: HTMLAudioElement;
  soundMusic: HTMLAudioElement;
  private audioEventListener: any;
  private audio: HTMLAudioElement;
  private applicationTexts$: ApplicationText[] | null = null;

  /**
   * Register all audio players
   * @param api
   * @param configService
   * @param sanitizer
   */
  constructor(
      private api: ApiService,
      private configService: ConfigService,
      private sanitizer: DomSanitizer) {
    this.audio = new Audio();
    this.soundError = new Audio();
    this.soundError.src = '/audio/wrong.mp3';
    this.soundSuccess = new Audio();
    this.soundSuccess.src = '/audio/success.wav';
    this.soundGameFinished = new Audio();
    this.soundGameFinished.src = '/audio/gamefinished.wav';
    this.soundLevelFinished = new Audio();
    this.soundLevelFinished.src = '/audio/levelfinished.wav';
    this.soundClick = new Audio();
    this.soundClick.src = '/audio/click.wav';
    this.soundMusic = new Audio();
  }

  public loadText(): Observable<ApplicationText[]> {
    if (this.applicationTexts$) {
      // console.log('already loaded');
      return of(this.applicationTexts$);
    } else {
      // Start API Call, Resolver roept aan
      // Daarna wordt de class var ingsteld en gaat de resolver weer verder
      return this.api.getApplicationTexts().pipe(tap(x => {
        this.applicationTexts$ = x;
      }));
    }
  }

  public playError() {
    this.soundError.currentTime = 0;
    this.soundError.play();
  }

  public playSuccess() {
    this.soundSuccess.currentTime = 0;
    this.soundSuccess.play();
  }

  public playSoundGameFinished() {
    this.soundGameFinished.currentTime = 0;
    this.soundGameFinished.play();
  }

  public playSoundLevelFinished() {
    this.soundLevelFinished.currentTime = 0;
    this.soundLevelFinished.play();
  }

  public playSoundClick() {
    this.soundClick.currentTime = 0;
    this.soundClick.play();
  }

  public cancelAll() {
    this.audio.pause();
  }

  public getPlay(identifier: ApplicationTextType, callback: CallBack | null = null): string {
    const appText = this.getAppText(identifier);
    this.play(identifier, callback);
    return appText.audioText.text;
  }

  public getPlayHTML(identifier: ApplicationTextType, callback = null): SafeHtml {
    const appText = this.getAppText(identifier);
    this.play(identifier, callback);
    return this.sanitizer.bypassSecurityTrustHtml(appText.audioText.text.replace('\n', '<br>'));
  }

  /**
   * Retrieve an application text based on its identifier. Retrieves a dummy if not found
   * @param identifier
   */
  private getAppText(identifier: ApplicationTextType): ApplicationText {
    if (this.applicationTexts$ === null) {
      throw Error('application text are not yet loaded');
    }
    const loadedText = this.applicationTexts$.find(x => x.identifier === identifier);
    if (loadedText !== undefined) {
      return loadedText;
    }

    console.error('application text not found', identifier);

    return {
      audioText: {
        text: '[' + identifier + ']',
        audio: null
      }
    } as ApplicationText;
  }

  /**
   * Retrieve an application text withouth playing the sound
   * @param identifier
   */
  public get(identifier: ApplicationTextType): string {
    return this.getAppText(identifier).audioText.text;
  }

  /**
   * Retrieve an application text withouth playing the sound
   * @param identifier
   */
   public getDownload(identifier: ApplicationTextType): string | null {
    return this.getAppText(identifier).download ;
  }

  /**
   * Retrieve an application text withouth playing the sound. Converts line breaks to html breaks
   * @param identifier
   */
  public getHTML(identifier: ApplicationTextType): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.getAppText(identifier).audioText.text.replace('\n', '<br>'));
  }

  public getPlayAudioHTML(audioText: AudioText, callback: CallBack | null = null): SafeHtml {
    this.playAudio(audioText, callback);
    return this.sanitizer.bypassSecurityTrustHtml(audioText.text.replace('\n', '<br>'));
  }

  /**
   * Play an audioText, and register the callback when finished playing.
   * @param audioText
   * @param callback
   * @returns boolean true when started, false if no audio is found
   */
  public playAudio(audioText: AudioText, callback: CallBack | null = null) {
    // console.log('play audio ', audioText);

    // If no audio element is available, do not play a sound;
    if (audioText.audio === null) {
      // console.log('No audio element available for', audioText.text);
      if (callback) {
        callback(null);
      }
      return false;
    }

    // Remove active callback
    this.audio.removeEventListener('ended', this.audioEventListener, true);

    // Set source of player
    this.audio.src = this.configService.getStaticUri() + audioText.audio;

    // Register callback event
    this.audioEventListener = callback;
    this.audio.addEventListener('ended', this.audioEventListener, true);

    // Play the sound
    this.stopMusic();
    this.audio.play();
    return true;
  }

  /**
   * Play a application text based on the identifier
   * @param identifier
   * @param callback
   */
  public play(identifier: ApplicationTextType, callback: CallBack | null = null) {

    this.playAudio(this.getAppText(identifier).audioText, callback);
  }

  /**
   * Stop playing the current audio text and remove the callback event
   */
  public stop() {
    this.audio.removeEventListener('ended', this.audioEventListener, true);
    this.audio.pause();
  }

  get text() {
    return translations();
  }

  stopMusic() {
    this.soundMusic.pause();
  }
  playMusic() {
    if (!this.soundMusic.paused) {
      this.stopMusic();
      return;
    }
    const audioText = this.getAppText('music');

    // Set source of player
    this.soundMusic.currentTime = 0;
    this.soundMusic.src = this.configService.getStaticUri() + audioText.audioText.audio;

    // Play the sound
    this.soundMusic.play();
    return true;
  }
}
